import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CmsBreakText } from '../../utils/cmsBreakText';
import { useAuth, useLocale } from '../../context';
import { api, toUserFacingError } from '../../api';
import { pushRoute } from '../../navigation';
import LoadingSpinner from '../loading/LoadingSpinner';

const ASK_MAX = 1500;
const ASK_MAX_FILES = 5;
const ASK_MAX_BYTES = 7 * 1024 * 1024;
const ASK_ACCEPT = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';

function IconClose() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M.456.454a1.094 1.094 0 0 1 1.548 0L9 7.468 16 .454A1.094 1.094 0 0 1 17.546 2l-7.014 6.998 7.015 6.998a1.087 1.087 0 0 1 0 1.545l-.002.003a1.09 1.09 0 0 1-1.545 0l-6.999-7.016-6.998 7.015v.001a1.09 1.09 0 0 1-1.545 0l-.003-.003a1.087 1.087 0 0 1 0-1.544l.001-.001 7.015-6.998L.456 2a1.094 1.094 0 0 1 0-1.547"
      />
    </svg>
  );
}

function IconAccordion({ open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.44a9.56 9.56 0 1 0 0 19.12 9.56 9.56 0 0 0 0-19.12M1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11S1 18.075 1 12Zm11.53-3.47a.75.75 0 0 0-1.06 0l-3.5 3.5a.75.75 0 1 0 1.06 1.06L12 10.56l2.97 2.97a.75.75 0 1 0 1.06-1.06l-3.5-3.5Z"
      />
    </svg>
  );
}

/** Fonbet landing “Bonus” type icon — exact path from LostAmountCashback2 viewForDesktop */
function IconBonus() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      style={{ color: 'var(--bonus-fill, #E88A00)' }}
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.499 1.33C7.997.57 9.32.193 11.099.023c.383-.037 1.47-.028 1.948.017 1.54.142 2.835.508 4.225 1.194 1.33.657 2.325 1.38 3.339 2.428a11.6 11.6 0 0 1 2.15 3.041 11.67 11.67 0 0 1 1.231 5.693c-.066 1.859-.49 3.48-1.339 5.113a12.24 12.24 0 0 1-4.066 4.517c-.625.415-1.838 1.023-2.534 1.268-2.293.811-4.648.924-6.996.335-.895-.224-1.464-.434-2.347-.867-1.265-.62-2.161-1.264-3.195-2.296-1.044-1.044-1.677-1.926-2.323-3.239-.571-1.16-.936-2.37-1.133-3.752-.079-.551-.079-2.403 0-2.954.113-.794.298-1.595.53-2.298.29-.872.89-2.107 1.382-2.838C3.13 3.658 4.724 2.232 6.499 1.33m15.023 6.046a10.63 10.63 0 0 0-8.569-5.93c-2.8-.256-5.637.642-7.816 2.473-2.344 1.97-3.755 5.006-3.755 8.079 0 3.071 1.418 6.12 3.755 8.077 1.533 1.283 3.297 2.082 5.328 2.412.721.117 2.33.117 3.052 0 1.183-.192 2.084-.473 3.098-.966a10.54 10.54 0 0 0 4.907-4.901c.495-1.017.776-1.916.968-3.098.117-.72.117-2.328 0-3.048-.192-1.182-.473-2.081-.968-3.098M12.616 7.2a.724.724 0 0 0-1.232 0l-1.51 2.439a.7.7 0 0 1-.235.234l-2.44 1.511a.724.724 0 0 0 0 1.232l2.44 1.51q.144.091.234.235l1.511 2.439a.724.724 0 0 0 1.232 0l1.51-2.44a.7.7 0 0 1 .235-.233l2.439-1.511a.724.724 0 0 0 0-1.232l-2.44-1.51a.7.7 0 0 1-.233-.235z"
      />
    </svg>
  );
}

function IconAttach() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M13.5 3.5v9a3.5 3.5 0 1 1-7 0V4.25a2.25 2.25 0 0 1 4.5 0v7.5a1 1 0 1 1-2 0V5.5a.75.75 0 0 0-1.5 0v6.25a2.5 2.5 0 0 0 5 0V4.25a3.75 3.75 0 1 0-7.5 0v8.25a5 5 0 0 0 10 0v-9a.75.75 0 0 0-1.5 0Z"
      />
    </svg>
  );
}

function getPageHtml(promo) {
  return String(promo?.pageHtml || promo?.page_html || '').trim();
}

function normalizeTerms(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') {
        const text = item.trim();
        if (!text) return null;
        const [title, ...rest] = text.split('\n');
        const body = rest.join('\n').trim();
        if (body) return { title: title.trim(), body };
        return { title: title.trim(), body: '' };
      }
      if (item && typeof item === 'object') {
        const title = String(item.title || item.heading || '').trim();
        const body = String(item.body || item.text || item.content || '').trim();
        if (!title && !body) return null;
        if (title && body) return { title, body };
        return { title: title || body, body: title && body ? body : '' };
      }
      return null;
    })
    .filter(Boolean);
}

function normalizeBonusTable(raw) {
  const table = raw && typeof raw === 'object' ? raw : null;
  if (!table) return null;
  const rows = Array.isArray(table.rows) ? table.rows : [];
  if (!rows.length) return null;
  return {
    label: String(table.label || table.title || 'Bonus table').trim() || 'Bonus table',
    colLosing: String(table.colLosing || table.columns?.[0] || 'Losing').trim(),
    colType: String(table.colType || table.columns?.[1] || 'Type').trim(),
    colAmount: String(table.colAmount || table.columns?.[2] || 'Cashback amount').trim(),
    rows: rows.map((row) => ({
      losing: String(row.losing || row.range || row.from || '').trim(),
      type: String(row.type || row.kind || 'Bonus').trim() || 'Bonus',
      amount: String(row.amount || row.cashback || row.value || '').trim(),
    })).filter((row) => row.losing || row.amount),
  };
}

function resolveNav(link, { onRegister, onClose }) {
  onClose?.();
  if (!link) {
    onRegister?.();
    return;
  }
  if (/^\/?register/i.test(link) || link === '/kayit') {
    onRegister?.();
    return;
  }
  if (/^https?:\/\//i.test(link)) {
    window.open(link, '_blank', 'noopener,noreferrer');
    return;
  }
  if (link.startsWith('//')) {
    window.open(`https:${link}`, '_blank', 'noopener,noreferrer');
    return;
  }
  if (link.startsWith('/')) {
    window.history.pushState({}, '', link);
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }
  window.location.href = link;
}

function resolveCtaAction(promo, helpers) {
  const link = String(promo?.link || promo?.linkUrl || '').trim();
  const label = String(promo?.ctaLabel || promo?.cta || '').trim();
  if (!label && !link) return null;
  const promoId = String(promo?.promo_id || promo?.promoId || promo?.id || '').trim();
  const needsActivate = ['promo_ms83ey6f', 'promo_ms86re5a', 'TR_CRM_NC_welcome_freebet100_freespins300', 'dep1', 'dep2', 'dep3', 'welcome'].includes(promoId) || promoId === 'welcome' || promo?.modalType === 'welcome-landing' || promo?.id === 'welcome';
  const needsCasinoLossClaim = promoId === 'promo_ms81wvh0';
  return {
    label: label || 'Devam',
    go: async () => {
      if (needsCasinoLossClaim || needsActivate) {
        try {
          const token = helpers?.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
          if (token) {
            const endpoint = needsCasinoLossClaim ? '/api/promos/casino-loss/claim' : '/api/promos/activate';
            const res = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ promo_id: promoId || ((promo?.modalType === 'welcome-landing' || promo?.id === 'welcome') ? 'welcome' : promoId) }),
            });
            const data = await res.json().catch(() => ({}));
            if (needsCasinoLossClaim) {
              if (!res.ok || data?.ok === false) {
                window.alert(data?.message || data?.error || (helpers?.locale === 'en'
                  ? 'Cashback could not be claimed yet.'
                  : 'Kayıp bonusu henüz alınamadı.'));
                return;
              }
              const amt = Number(data?.bonus_amount || 0);
              window.alert(helpers?.locale === 'en'
                ? `15% loss cashback credited: ${amt.toLocaleString('en-US')} TL`
                : `%15 kayıp bonusu yatırıldı: ${amt.toLocaleString('tr-TR')} TL`);
            }
          } else if (needsCasinoLossClaim) {
            helpers?.onRegister?.();
            return;
          }
        } catch (_) {
          if (needsCasinoLossClaim) {
            window.alert(helpers?.locale === 'en'
              ? 'Cashback request failed.'
              : 'Kayıp bonusu talebi başarısız.');
            return;
          }
        }
      }
      resolveNav(link, helpers);
    },
  };
}

function AccordionSection({ id, title, open, onToggle, children }) {
  return (
    <div className={`promo-dialog__acc${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="promo-dialog__acc-head"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span className="promo-dialog__acc-title">{title}</span>
        <span className="promo-dialog__acc-icon">
          <IconAccordion open={open} />
        </span>
      </button>
      {open ? (
        <div id={id} className="promo-dialog__acc-body">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function BonusAskPanel({
  locale,
  promoTitle,
  onCancel,
  onRegister,
}) {
  const { token, user } = useAuth();
  const { t: tr } = useLocale();
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const isEn = locale === 'en';
  const remaining = ASK_MAX - text.length;
  const canSend = text.trim().length > 0 && !busy;

  const copy = {
    title: isEn ? 'Bonus question' : 'Bonus sorusu',
    placeholder: isEn
      ? 'Enter your question or describe the problem'
      : 'Sorunuzu yazın veya sorunu açıklayın',
    remaining: isEn
      ? `${remaining} characters remaining`
      : `${remaining} karakter kaldı`,
    filesHint: isEn
      ? 'Up to 5 files. Format: jpg, jpeg, png, pdf. Up to 7Mb.'
      : 'En fazla 5 dosya. Format: jpg, jpeg, png, pdf. En fazla 7Mb.',
    attach: isEn ? 'Attach file' : 'Dosya ekle',
    cancel: isEn ? 'Cancel' : 'İptal',
    send: isEn ? 'Send request' : 'Talep gönder',
    success: isEn ? 'Your request has been sent.' : 'Talebiniz gönderildi.',
    loginRequired: isEn
      ? 'Please sign in to send a request.'
      : 'Talep göndermek için giriş yapın.',
    loginCta: isEn ? 'Sign in' : 'Giriş yap',
  };

  const onPickFiles = (list) => {
    const next = [...files];
    for (const file of list) {
      if (next.length >= ASK_MAX_FILES) break;
      if (file.size > ASK_MAX_BYTES) {
        setError(isEn ? 'File is larger than 7Mb.' : 'Dosya 7Mb’den büyük.');
        continue;
      }
      const okType = /^(image\/jpeg|image\/png|application\/pdf)$/i.test(file.type)
        || /\.(jpe?g|png|pdf)$/i.test(file.name);
      if (!okType) {
        setError(isEn ? 'Unsupported file format.' : 'Desteklenmeyen dosya formatı.');
        continue;
      }
      next.push(file);
    }
    setFiles(next.slice(0, ASK_MAX_FILES));
  };

  const submit = async () => {
    if (!canSend) return;
    if (!token || !user) {
      onCancel();
      onRegister?.();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const subject = isEn ? 'Bonus question' : 'Bonus sorusu';
      const promoLine = promoTitle ? `\n\n[${promoTitle}]` : '';
      const fileNames = files.length
        ? `\n\n${isEn ? 'Attachments' : 'Ekler'}: ${files.map((f) => f.name).join(', ')}`
        : '';
      await api.createSupportTicket(token, {
        subject,
        message: `${text.trim()}${promoLine}${fileNames}`,
        type_key: 'bonus_question',
        locale: isEn ? 'en' : 'tr',
        payload: {
          source: 'promo_landing',
          promoTitle: promoTitle || '',
          files: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
        },
      });
      setDone(true);
    } catch (err) {
      setError(toUserFacingError(err.message, tr('account.requests.error') || (isEn ? 'Could not send request' : 'Talep gönderilemedi')));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="promo-dialog__ask">
        <div className="promo-dialog__ask-main">
          <div className="promo-dialog__ask-title">{copy.title}</div>
          <p className="promo-dialog__ask-success">{copy.success}</p>
        </div>
        <div className="promo-dialog__ask-actions">
          <div className="promo-dialog__ask-actions-right">
            <button type="button" className="promo-dialog__ask-btn promo-dialog__ask-btn--primary" onClick={onCancel}>
              {isEn ? 'Close' : 'Kapat'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="promo-dialog__ask">
      <div className="promo-dialog__ask-main">
        <div className="promo-dialog__ask-title">{copy.title}</div>
        <div className="promo-dialog__ask-field">
          <textarea
            className="promo-dialog__ask-textarea"
            maxLength={ASK_MAX}
            placeholder={copy.placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
          />
          <div className="promo-dialog__ask-remaining">{copy.remaining}</div>
        </div>
        <div className="promo-dialog__ask-hint">
          <span>{isEn ? 'Up to 5 files.' : 'En fazla 5 dosya.'}</span>
          <span>{isEn ? 'Format: jpg, jpeg, png, pdf.' : 'Format: jpg, jpeg, png, pdf.'}</span>
          <span>{isEn ? 'Up to 7Mb.' : 'En fazla 7Mb.'}</span>
        </div>
        {files.length > 0 ? (
          <ul className="promo-dialog__ask-files">
            {files.map((file, i) => (
              <li key={`${file.name}-${i}`}>
                <span>{file.name}</span>
                <button
                  type="button"
                  className="promo-dialog__ask-file-remove"
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {error ? <p className="promo-dialog__ask-error">{error}</p> : null}
      </div>
      <div className="promo-dialog__ask-actions">
        <button
          type="button"
          className="promo-dialog__ask-btn promo-dialog__ask-btn--attach"
          onClick={() => fileRef.current?.click()}
          disabled={files.length >= ASK_MAX_FILES || busy}
        >
          <IconAttach />
          <span>{copy.attach}</span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ASK_ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            onPickFiles([...e.target.files]);
            e.target.value = '';
          }}
        />
        <div className="promo-dialog__ask-actions-right">
          <button type="button" className="promo-dialog__ask-btn promo-dialog__ask-btn--ghost" onClick={onCancel} disabled={busy}>
            {copy.cancel}
          </button>
          <button
            type="button"
            className="promo-dialog__ask-btn promo-dialog__ask-btn--primary"
            onClick={submit}
            disabled={!canSend}
          >
            {busy ? (isEn ? 'Sending…' : 'Gönderiliyor…') : copy.send}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PromoDetailsModal({ promo, onClose, onRegister }) {
  const { token } = useAuth();
  const [phase, setPhase] = useState('enter');
  const [bannerReady, setBannerReady] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [openSections, setOpenSections] = useState({ conditions: true, table: true });

  const promoKey = promo?.promoId || promo?.id || '';
  const banner = promo?.bannerImg || promo?.heroImg || promo?.landingImg || promo?.img;
  const pageHtml = getPageHtml(promo);
  const isPageContent = Boolean(pageHtml);
  const terms = normalizeTerms(promo?.terms);
  const termsStyle = String(promo?.termsStyle || promo?.terms_style || 'bullet').toLowerCase() === 'numbered'
    ? 'numbered'
    : 'bullet';
  const termsLabel = String(promo?.termsLabel || promo?.terms_label || '').trim()
    || (termsStyle === 'numbered' ? 'Koşullar' : 'Koşullar');
  const bonusTable = normalizeBonusTable(promo?.bonusTable || promo?.bonus_table);
  const rulesUrl = String(promo?.rulesUrl || promo?.rules_url || '/pages/rules').trim() || '/pages/rules';

  useEffect(() => {
    if (!promoKey) return undefined;

    setPhase('enter');
    setBannerReady(false);
    setContentReady(false);
    setShowAsk(false);
    setOpenSections({ conditions: true, table: true });

    const img = new Image();
    let cancelled = false;
    const bannerSrc = banner;

    const minWait = new Promise((resolve) => {
      window.setTimeout(resolve, 420);
    });

    const imgWait = new Promise((resolve) => {
      if (!bannerSrc) {
        resolve();
        return;
      }
      img.onload = resolve;
      img.onerror = resolve;
      img.src = bannerSrc;
    });

    Promise.all([minWait, imgWait]).then(() => {
      if (cancelled) return;
      setBannerReady(true);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setContentReady(true));
      });
    });

    const openTimer = window.requestAnimationFrame(() => setPhase('open'));

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(openTimer);
    };
    // Only re-boot when the promo identity changes — banner URL churn from
    // content polling must not flash the modal loader again.
  }, [promoKey]);

  const handleClose = useCallback(() => {
    setPhase('leave');
    window.setTimeout(onClose, 220);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (showAsk) {
          setShowAsk(false);
          return;
        }
        handleClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, showAsk]);

  const openRulesPage = useCallback(() => {
    setPhase('leave');
    window.setTimeout(() => {
      onClose?.();
      if (rulesUrl === '/pages/rules' || rulesUrl.endsWith('/pages/rules')) {
        pushRoute('cms', { cmsSlug: 'rules' });
        return;
      }
      resolveNav(rulesUrl, { onRegister, onClose: () => {} });
    }, 220);
  }, [onClose, onRegister, rulesUrl]);

  if (!promo) return null;

  const caption = promo.caption || promo.uptitle || '';
  const title = promo.detailTitle || String(promo.title || '');
  const subtitle = promo.detailSubtitle || promo.sub || promo.subtitle || '';
  const locale = String(promo.locale || '').toLowerCase();
  const cta = resolveCtaAction(promo, { onRegister, onClose: handleClose, token, locale });
  const rulesLabel = locale === 'en' ? 'Promotion Rules' : 'Kampanya Kuralları';
  const askLabel = locale === 'en' ? 'Ask a Question' : 'Soru Sor';

  const toggle = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openAsk = () => {
    const scroll = document.querySelector('.promo-dialog__scroll');
    if (scroll) scroll.scrollTop = 0;
    setShowAsk(true);
  };

  return createPortal(
    <div
      className={`promo-dialog${phase === 'open' ? ' is-open' : ''}${phase === 'leave' ? ' is-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-dialog-title"
    >
      <div className="promo-dialog__overlay" onClick={handleClose} />
      <div className="promo-dialog__window">
        <div className={`promo-dialog__engine${contentReady ? ' is-ready' : ' is-loading'}`}>
          <button
            type="button"
            className="promo-dialog__close"
            aria-label="Close"
            data-testid="btn.closeLanding"
            onClick={handleClose}
          >
            <IconClose />
          </button>

          <div
            className={`promo-dialog__loader-wrap${contentReady ? ' is-hidden' : ''}`}
            aria-hidden={contentReady}
            data-name="loyaltyLandingLoader"
          >
            <LoadingSpinner size="lg" className="promo-dialog__loader-spinner" />
          </div>

          <div className={`promo-dialog__grid${contentReady ? ' is-visible' : ''}`}>
            <div className="promo-dialog__scroll-outer">
              <div className="promo-dialog__scroll">
                <div className={`promo-dialog__content${showAsk ? ' is-ask-open' : ''}`}>
                  {banner ? (
                    <div
                      className={`promo-dialog__banner${bannerReady ? ' is-loaded' : ''}`}
                      style={{ backgroundImage: bannerReady ? `url(${banner})` : undefined }}
                    />
                  ) : null}

                  <div className="promo-dialog__body">
                    {isPageContent ? (
                      <div
                        id="promo-dialog-title"
                        className="promo-dialog__page-html"
                        dangerouslySetInnerHTML={{ __html: pageHtml }}
                      />
                    ) : (
                      <>
                        {caption ? (
                          <div className="promo-dialog__caption">
                            <CmsBreakText text={caption} />
                          </div>
                        ) : null}
                        <h2 id="promo-dialog-title" className="promo-dialog__title">
                          <CmsBreakText text={title} />
                        </h2>
                        {subtitle ? (
                          <div className="promo-dialog__subtitle">
                            <CmsBreakText text={subtitle} />
                          </div>
                        ) : null}

                        {terms.length > 0 ? (
                          <AccordionSection
                            id="promo-acc-conditions"
                            title={termsLabel}
                            open={openSections.conditions}
                            onToggle={() => toggle('conditions')}
                          >
                            <div className={`promo-dialog__panel promo-dialog__terms promo-dialog__terms--${termsStyle}`}>
                              {terms.map((term, index) => (
                                <div key={`term-${index}`} className="promo-dialog__term">
                                  <div className="promo-dialog__term-num" aria-hidden="true">
                                    {termsStyle === 'numbered' ? `${index + 1}.` : '•'}
                                  </div>
                                  <div className="promo-dialog__term-main">
                                    <div className="promo-dialog__term-title">
                                      <CmsBreakText text={term.title} />
                                    </div>
                                    {term.body ? (
                                      <div className="promo-dialog__term-body">
                                        <CmsBreakText text={term.body} />
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </AccordionSection>
                        ) : null}

                        {bonusTable ? (
                          <AccordionSection
                            id="promo-acc-table"
                            title={bonusTable.label}
                            open={openSections.table}
                            onToggle={() => toggle('table')}
                          >
                            <div className="promo-dialog__panel promo-dialog__table">
                              <div className="promo-dialog__table-head">
                                <div>{bonusTable.colLosing}</div>
                                <div>{bonusTable.colType}</div>
                                <div>{bonusTable.colAmount}</div>
                              </div>
                              <div className="promo-dialog__table-body">
                                {bonusTable.rows.map((row, index) => (
                                  <div key={`row-${index}`} className="promo-dialog__table-row">
                                    <div className="promo-dialog__table-cell">{row.losing}</div>
                                    <div className="promo-dialog__table-cell promo-dialog__table-type">
                                      <span className="promo-dialog__bonus-badge">
                                        <IconBonus />
                                        <span>{row.type}</span>
                                      </span>
                                    </div>
                                    <div className="promo-dialog__table-cell promo-dialog__table-amount">
                                      {row.amount}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </AccordionSection>
                        ) : null}
                      </>
                    )}

                    {!isPageContent ? (
                      <div className="promo-dialog__footer-links">
                        <button type="button" className="promo-dialog__link" onClick={openRulesPage}>
                          <span>{rulesLabel}</span>
                        </button>
                        <button type="button" className="promo-dialog__link" onClick={openAsk}>
                          <span>{askLabel}</span>
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {cta ? (
                    <div className="promo-dialog__footer promo-dialog__footer--cta">
                      <div className="promo-dialog__footer-inner">
                        <button type="button" className="promo-dialog__cta" onClick={cta.go}>
                          <span>{cta.label}</span>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {showAsk ? (
                <div className="promo-dialog__ask-layer" role="dialog" aria-modal="true" aria-label={askLabel}>
                  <button
                    type="button"
                    className="promo-dialog__ask-backdrop"
                    aria-label="Close"
                    onClick={() => setShowAsk(false)}
                  />
                  <div className="promo-dialog__ask-sheet">
                    <button
                      type="button"
                      className="promo-dialog__ask-close"
                      aria-label="Close"
                      onClick={() => setShowAsk(false)}
                    >
                      <IconClose />
                    </button>
                    <BonusAskPanel
                      locale={locale}
                      promoTitle={title}
                      onCancel={() => setShowAsk(false)}
                      onRegister={onRegister}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
